import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Resolve the timezone to display times in: the user's saved timezone, else the
 * browser's, else UTC. Pass the reactive `useAuth().user?.timezone` so views
 * re-render when the setting changes. Times are stored as UTC instants; this is
 * purely the display zone — the single source of truth across apps (CRM calendar,
 * CRM dashboard, shell overview).
 */
export const resolveUserTimeZone = (userTimezone?: string | null): string => {
  if (userTimezone) {
    return userTimezone;
  }
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

/**
 * Format a stored UTC instant for a dashboard "Today's Tasks" event row, in the
 * viewer's display timezone. Same-day events show just the time ("3:00 PM");
 * other days include the date ("Jun 4, 3:00 PM"). Shared by the CRM dashboard
 * and the shell CRM Overview so both render event times identically.
 */
export const formatEventTime = (startTime: string, timeZone: string): string => {
  const event = dayjs(startTime).tz(timeZone);
  const isToday = event.isSame(dayjs().tz(timeZone), 'day');

  return isToday ? event.format('h:mm A') : event.format('MMM D, h:mm A');
};
